/**
 * Add Session Dialog
 *
 * Allows users to create a new session from PRS version pairs.
 * Auto-selects base and target PRS if only one of each exists.
 * Auto-generates session name as `{base}_to_{target}` (dots in versions become underscores).
 *
 * Uses modular-ui Dialog component.
 */

import React, {useState, useEffect} from 'react';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import {Dialog as ModularDialog} from '@signanthealth/modular-ui';
import {apiService} from '../services/api';

interface PrsVersionInfo {
  version: string;
  is_base: boolean;
  uploaded_at?: string;
}

interface AddSessionDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  trialId: string;
  /** When true, trial detail is being fetched (e.g. for prs_versions with is_base) */
  loading?: boolean;
  platforms: string[];
  prsVersions: PrsVersionInfo[];
  existingSessionIds: string[];
  /** Check if a platform has analysis available (mirrors Flutter's AnalysisStatusCubit check) */
  isAnalysisAvailable?: (platform: string) => boolean;
}

const SESSION_ID_PATTERN = /^[A-Za-z0-9_-]+$/;

const isValidSessionId = (value: string): boolean =>
  value.length > 0 && SESSION_ID_PATTERN.test(value);

/** Session IDs cannot contain `.`; PRS version strings often do. */
const sanitizeVersionForSuggestedSessionName = (version: string) =>
  version.replace(/\./g, '_');

export const AddSessionDialog: React.FC<AddSessionDialogProps> = ({
  open,
  onClose,
  onSuccess,
  trialId,
  loading: loadingTrialDetail = false,
  platforms,
  prsVersions,
  existingSessionIds,
  isAnalysisAvailable,
}) => {
  // Filter platforms to only those with analysis available (like Flutter)
  const platformsWithAnalysis = isAnalysisAvailable
    ? platforms.filter((p) => isAnalysisAvailable(p))
    : platforms;
  const basePrs = prsVersions.find((v) => v.is_base);
  const targetVersions = prsVersions.filter((v) => !v.is_base);

  const [selectedTarget, setSelectedTarget] = useState<string>('');
  const [sessionName, setSessionName] = useState<string>('');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const trimmedSessionName = sessionName.trim();
  const hasSessionNameFormatError =
    trimmedSessionName.length > 0 && !isValidSessionId(trimmedSessionName);

  // Auto-select target if only one, and generate session name
  useEffect(() => {
    if (targetVersions.length === 1) {
      setSelectedTarget(targetVersions[0].version);
    }
  }, [prsVersions]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-generate session name when selections change
  useEffect(() => {
    if (basePrs && selectedTarget) {
      let proposedName = `${sanitizeVersionForSuggestedSessionName(basePrs.version)}_to_${sanitizeVersionForSuggestedSessionName(selectedTarget)}`;
      if (existingSessionIds.includes(proposedName)) {
        let index = 2;
        while (existingSessionIds.includes(`${proposedName}_${index}`)) {
          index++;
        }
        proposedName = `${proposedName}_${index}`;
      }
      setSessionName(proposedName);
    }
  }, [basePrs, selectedTarget, existingSessionIds]);

  const handleCreate = async () => {
    if (isCreating) return; // Prevent double-click

    if (!basePrs || !selectedTarget || !trimmedSessionName) {
      setError('Please select both base and target PRS');
      return;
    }

    if (!isValidSessionId(trimmedSessionName)) {
      setError(
        'Invalid session name format. Must contain only letters, numbers, hyphens, and underscores.'
      );
      return;
    }

    if (existingSessionIds.includes(trimmedSessionName)) {
      setError('A session with this name already exists');
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      // Create session for all platforms with analysis available in parallel
      const platformsToCreate = Array.from(new Set(platformsWithAnalysis.length > 0 ? platformsWithAnalysis : platforms));
      await Promise.all(
        platformsToCreate.map((platform) =>
          apiService.createSession({
            trial_id: trialId,
            name: trimmedSessionName,
            platform: platform as 'app' | 'web',
            version1: basePrs.version,
            version2: selectedTarget,
          })
        )
      );
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(`Failed to create session: ${err.message || 'Unknown error'}`);
    } finally {
      setIsCreating(false);
    }
  };

  const canCreate =
    !!basePrs &&
    !!selectedTarget &&
    !!trimmedSessionName &&
    !hasSessionNameFormatError &&
    !isCreating;

  return (
    <ModularDialog
      open={open}
      title={<Box sx={{pl: 3, pr: 3, mb: 2}}>Create Session</Box>}
      content={
        <Box sx={{minWidth: 400, pl: 3, pr: 3, pb: 1}}>
          {/* Trial info */}
          <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
            Trial: <strong>{trialId}</strong>
          </Typography>

          {loadingTrialDetail && (
            <Box sx={{display: 'flex', alignItems: 'center', gap: 2, py: 3}}>
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary">
                Loading trial details...
              </Typography>
            </Box>
          )}

          {!loadingTrialDetail && (
            <>
          {/* Error */}
          {error && (
            <Alert severity="error" sx={{mb: 2}} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Base PRS (read-only) */}
          {basePrs ? (
            <TextField
              fullWidth
              disabled
              label="Base PRS (Current Protocol)"
              value={basePrs.version}
              sx={{mb: 3}}
            />
          ) : (
            <Alert severity="warning" sx={{mb: 2}}>
              No base PRS uploaded yet
            </Alert>
          )}

          {/* Target PRS */}
          {targetVersions.length === 0 ? (
            <Alert severity="warning" sx={{mb: 2}}>
              No target PRS uploaded yet
            </Alert>
          ) : targetVersions.length === 1 ? (
            <TextField
              fullWidth
              disabled
              label="Target PRS (Changes to Implement)"
              value={targetVersions[0].version}
              sx={{mb: 3}}
            />
          ) : (
            <TextField
              select
              fullWidth
              label="Target PRS (Changes to Implement)"
              value={selectedTarget}
              onChange={(e) => setSelectedTarget(e.target.value)}
              sx={{mb: 3}}
            >
              {targetVersions.map((v) => (
                <MenuItem key={v.version} value={v.version}>
                  {v.version}
                </MenuItem>
              ))}
            </TextField>
          )}

          {/* Session Name */}
          <TextField
            fullWidth
            label="Session Name"
            value={sessionName}
            onChange={(e) => setSessionName(e.target.value)}
            placeholder="e.g., 2-2_to_2-2A"
            sx={{mb: 3}}
            error={
              hasSessionNameFormatError ||
              existingSessionIds.includes(trimmedSessionName)
            }
            helperText={
              hasSessionNameFormatError
                ? 'Only letters, numbers, hyphens, and underscores are allowed.'
                : existingSessionIds.includes(trimmedSessionName)
                ? 'Session with this name already exists'
                : 'Alphanumeric with hyphens/underscores only.'
            }
          />

          {/* Platforms (with analysis readiness indicators like Flutter) */}
          <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
            Platforms
          </Typography>
          <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}>
            {platforms.map((p) => {
              const hasAnalysis = isAnalysisAvailable ? isAnalysisAvailable(p) : true;
              return (
                <Chip
                  key={p}
                  label={
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                      {p.toUpperCase()}
                      {!hasAnalysis && (
                        <Box component="span" sx={{fontSize: '0.7rem', opacity: 0.7}}>
                          (no analysis)
                        </Box>
                      )}
                    </Box>
                  }
                  size="small"
                  color={hasAnalysis ? (p === 'app' ? 'primary' : 'secondary') : 'default'}
                  variant={hasAnalysis ? 'filled' : 'outlined'}
                  sx={{
                    textDecoration: hasAnalysis ? 'none' : 'line-through',
                    opacity: hasAnalysis ? 1 : 0.6,
                  }}
                />
              );
            })}
          </Box>
          {platformsWithAnalysis.length > 0 ? (
            <Typography variant="caption" color="text.secondary" sx={{mt: 0.5, display: 'block'}}>
              Session will be created for {platformsWithAnalysis.length} platform(s)
            </Typography>
          ) : isAnalysisAvailable ? (
            <Alert severity="warning" sx={{mt: 1}}>
              No platforms have analysis ready. Generate analysis from the Trial Manager first.
            </Alert>
          ) : (
            <Typography variant="caption" color="text.secondary" sx={{mt: 0.5, display: 'block'}}>
              Session will be created for {platforms.length} platform(s)
            </Typography>
          )}
            </>
          )}
        </Box>
      }
      dialogPaperSx={{
        width: '100%',
        maxHeight: '100%',
        pt: 3,
        pb: 3,
        pl: 0,
        pr: 0,
      }}
      dialogTitleSx={{p: 0}}
      dialogContentSx={{p: 0}}
      dialogActionsSx={{
        pl: 3,
        pr: 3,
        justifyContent: 'space-between',
        pt: 4,
        pb: 0,
      }}
      actionButtonProps={[
        {
          children: 'Cancel',
          variant: 'outlined' as const,
          onClick: onClose,
          disabled: isCreating,
        },
        {
          children: isCreating ? (
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
              <CircularProgress size={16} color="inherit" />
              Creating...
            </Box>
          ) : (
            'Create Session'
          ),
          variant: 'contained' as const,
          onClick: handleCreate,
          disabled: loadingTrialDetail || !canCreate,
        },
      ]}
    />
  );
};
